import { useState, useEffect, useMemo, useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import { Card, CardContent, CardFooter } from "@design-lab/shared/ui/card";
import { transfersManagerAtom } from "@design-lab/entities/transfers-lab/model/transfers-manager.machine";
import { uploadModalOpenAtom } from "@design-lab/entities/transfers-lab";
import { TransferTypeModal } from "@design-lab/entities/transfer-types";
import { AddToCartTransfers } from "@design-lab/widgets/transfers-footer/ui/AddToCartTransfers";
import { ArtworksHeader } from "./ArtworksHeader";
import { TransfersSizesContent } from "./TransfersSizesContent";
import { useArtworkSizesForm } from "../model/useArtworkSizesForm";
import { useTransferTypes } from "../model/useTransferTypes";
import { useTransferEnhancementTier } from "../hooks/useTransferEnhancementTier";

export function TransfersSizes() {
  // Get state from atom
  const [state, send] = useAtom(transfersManagerAtom);
  const { items, selectedId } = state.context;
  const setUploadModalOpen = useSetAtom(uploadModalOpenAtom);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { transferTypes } = useTransferTypes();

  // Auto-select first item if nothing is selected
  useEffect(() => {
    const firstItem = items[0];
    if (!selectedId && firstItem) {
      send({ type: "artwork.select", id: firstItem.id });
    }
  }, [selectedId, items, send]);

  // Get the selected artwork actor
  const selectedArtwork = selectedId ? items.find(item => item.id === selectedId) : null;

  // Use the sizes form for the selected artwork
  const { sizesFormControl, artworkId, updateQuantity, isInitialized } =
    useArtworkSizesForm(selectedArtwork);

  // Get enhancement tier from the selected artwork
  const enhancementTier = useTransferEnhancementTier(selectedArtwork);

  // Get transfer types without watching - use state instead
  const [currentTransferTypes, setCurrentTransferTypes] = useState<Record<string, any>>({});

  // Subscribe to form changes instead of watching
  useEffect(() => {
    if (!sizesFormControl || !artworkId) {
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const subscription = sizesFormControl.watch((value, { name }) => {
      // Update if this artwork was modified
      if (name?.startsWith(`artworks.${artworkId}`)) {
        const newTransferTypes = value.artworks?.[artworkId]?.transferTypes || {};
        setCurrentTransferTypes(newTransferTypes);

        // Debounce the quantity update
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          updateQuantity();
          // Notify parent machine that quantities have changed
          send({ type: "quantity.state.changed" });
        }, 100);
      }
    });

    // Initialize with current values
    const initialTypes = sizesFormControl.getValues(`artworks.${artworkId}.transferTypes`) || {};
    setCurrentTransferTypes(initialTypes);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [sizesFormControl, artworkId, updateQuantity]);

  // Memoize the transfer type order to prevent unnecessary re-renders
  const transferTypeOrder = useMemo(
    () => Object.keys(currentTransferTypes),
    [currentTransferTypes]
  );

  const onSubmit = useCallback(async () => {
    if (!sizesFormControl) return;

    setIsAddingToCart(true);
    try {
      const data = sizesFormControl.getValues();
      // eslint-disable-next-line no-console
      console.log("Form submitted:", data);
      // Here you would send the data to your API
      // TODO: Implement actual add to cart logic
    } catch (error) {
      console.error("Error adding to cart:", error);
      window.Honeybadger?.notify("Error adding to cart", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          component: "TransfersSizes",
          action: "onSubmit"
        }
      });
    } finally {
      setIsAddingToCart(false);
    }
  }, [sizesFormControl]);

  const onUpload = useCallback(() => {
    // Switch to upload tab and show modal
    send({ type: "tab.change", tab: "upload" });
    // Use setTimeout to ensure tab switch happens first
    setTimeout(() => {
      setUploadModalOpen(true);
    }, 100);
  }, [send, setUploadModalOpen]);

  const handleSelectArtwork = useCallback(
    (id: string) => {
      // Update the global selected ID in the state machine
      send({ type: "artwork.select", id });
    },
    [send]
  );

  const handleSelectTransferTypes = useCallback(
    (types: string[]) => {
      if (!selectedArtwork || !sizesFormControl || !artworkId) return;

      // Get current values once
      const currentValues = sizesFormControl.getValues();
      const artwork = currentValues.artworks?.[artworkId];
      if (!artwork) return;

      // Build the new transfer types object
      const newTransferTypes: Record<string, any> = {};

      // Add all selected types
      types.forEach((typeId: string) => {
        // Keep existing transfer type if it already exists
        if (artwork.transferTypes[typeId]) {
          newTransferTypes[typeId] = artwork.transferTypes[typeId];
        } else {
          // Add new transfer type
          const transferType = transferTypes.find(t => t.id === typeId);
          if (transferType) {
            newTransferTypes[typeId] = {
              id: typeId,
              name: transferType.shortLabel,
              presets: {},
              presetOrder: [],
              customPreset: {
                id: `custom-${typeId}`,
                customSizes: []
              }
            };
          }
        }
      });

      // Update the entire artwork with new transfer types at once
      const updatedArtwork = {
        ...artwork,
        transferTypes: newTransferTypes
      };

      sizesFormControl.setValue(`artworks.${artworkId}`, updatedArtwork);

      // Update local state immediately for UI consistency
      setCurrentTransferTypes(newTransferTypes);

      updateQuantity();
      // Notify parent machine that quantities have changed
      send({ type: "quantity.state.changed" });
      setIsModalOpen(false);
    },
    [selectedArtwork, sizesFormControl, artworkId, transferTypes, updateQuantity, send]
  );

  const handleAddTransferType = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  return (
    <>
      <Card className="flex h-full flex-1 flex-col border-none shadow-none lg:border lg:shadow">
        <CardContent className="flex flex-1 flex-col overflow-y-auto pt-6">
          <div className="flex flex-1 flex-col gap-2">
            <div className="relative h-full max-w-[545px] rounded-2xl bg-white">
              <ArtworksHeader
                items={items}
                selectedId={selectedId}
                onUpload={onUpload}
                onSelect={handleSelectArtwork}
              />

              <TransfersSizesContent
                items={items}
                selectedArtwork={selectedArtwork}
                artworkId={artworkId}
                isInitialized={isInitialized}
                sizesFormControl={sizesFormControl}
                onUpload={onUpload}
                onAddTransferType={handleAddTransferType}
                enhancementTier={enhancementTier}
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-start gap-2 pt-6">
          <AddToCartTransfers
            onAddToCart={onSubmit}
            isLoading={isAddingToCart}
            isDisabled={!selectedArtwork || !artworkId || !isInitialized || !sizesFormControl}
          />
        </CardFooter>
      </Card>

      <TransferTypeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleSelectTransferTypes}
        selectedTypes={transferTypeOrder}
      />
    </>
  );
}
